import DatePickerDialog from './datepicker-dialog.js';
import '../../styles/common.css';
import '../../styles/datepicker.css';
import { getAllMonthNames, getAllDayNames } from '../../utils/index.js';

export class DatePicker extends HTMLElement {
    static observedAttributes = [
        'mindate',
        'maxdate',
        'class',
        'default-date',
        'no-footer-buttons',
        'floating-label',
        'format',
        'valid-formats',
        'localization',
        'placeholder',
    ];
    static idCounter = 1;

    static regexMap = {
        YYYY: '(\\d{4})',
        YY: '(\\d{2})',
        MMMM: '([A-Za-z]+)',
        MM: '(\\d{1,2})',
        DD: '(\\d{1,2})',
        D: '(\\d{1,2})',
    };

    constructor() {
        super();

        this.input = null;

        this.inputValue = null;

        this.minDate = null;
        this.maxDate = null;
        this.defaultDate = null;

        this.inputLabel = null;

        this.elementId = '';
    }

    async connectedCallback() {
        // Move this to template tag later

        const langMap = {
            sv: 'sv-SE',
            en: 'en-US',
        };

        this.lang =
            langMap[document.documentElement.getAttribute('lang')] || 'en-US';

        this.defaultDate =
            this.hasAttribute('default-date') &&
            this.getAttribute('default-date') !== ''
                ? this.getAttribute('default-date')
                : null;

        this.elementId = this.id
            ? this.id
            : `datepicker-${DatePicker.idCounter++}`;

        const defaultFormat = 'MM/DD/YYYY';
        const validFormats = this.getAttribute('valid-formats');

        this.validFormats = validFormats
            ? validFormats.split(',').map((f) => f.trim())
            : [];

        const formatStr = this.getAttribute('format');
        this.format = formatStr || defaultFormat;

        const placeholderStr = this.getAttribute('placeholder') || '';
        const validate = this.getAttribute('validate');
        this.validate = validate === 'true' || validate === true;

        const localizationAttr = this.hasAttribute('localization')
            ? this.getAttribute('localization')
            : '{}';
        const validJsonLocalization = localizationAttr
            .replace(/(\w+):/g, '"$1":')
            .replace(/'/g, '"');

        this.localization = JSON.parse(validJsonLocalization) || {};

        this.mondayFirstDayOfTheWeek = true;

        const containerDiv = document.createElement('div');
        containerDiv.classList.add('datepicker');

        const dateDiv = document.createElement('div');
        dateDiv.classList.add('input-container');

        containerDiv.appendChild(dateDiv);

        const inputId = `${this.elementId}-input`;

        this.inputLabel = document.createElement('label');
        this.inputLabel.htmlFor = inputId;

        this.useFloatingLabel = this.hasAttribute('floating-label')
            ? this.getAttribute('floating-label') !== 'false'
            : false;
        if (
            this.useFloatingLabel &&
            placeholderStr != null &&
            placeholderStr.trim() != ''
        ) {
            this.inputLabel.textContent = placeholderStr;
        } else {
            this.inputLabel.textContent = this.getAttribute('label') || '';
            this.inputLabel.classList.add('sr-only');
        }

        dateDiv.appendChild(this.inputLabel);

        const groupDiv = document.createElement('div');
        groupDiv.classList.add('group');

        dateDiv.appendChild(groupDiv);

        this.input = document.createElement('input');
        this.input.id = inputId;
        this.input.type = 'text';

        const labeledById = `${this.elementId}-description`;
        this.input.setAttribute('aria-labelledby', labeledById);

        if (this.useFloatingLabel) {
            this.input.classList.add('has-floating-label');
        } else if (placeholderStr != null && placeholderStr.trim() != '') {
            this.input.placeholder = placeholderStr;
        }

        groupDiv.appendChild(this.input);

        const descSpan = document.createElement('span');
        descSpan.classList.add('desc');
        descSpan.classList.add('sr-only');
        descSpan.id = labeledById;

        descSpan.innerHTML = `(date format: ${formatStr || defaultFormat})`;

        groupDiv.appendChild(descSpan);

        const button = document.createElement('button');
        button.type = 'button';
        button.classList.add('icon');
        button.setAttribute(
            'aria-label',
            this.localization.chooseDate || 'Choose date'
        );

        if (this.localization.openCalendar) {
            const buttonSpan = document.createElement('span');
            buttonSpan.textContent = this.localization.openCalendar;
            button.appendChild(buttonSpan);
        }

        const svg = document.createElementNS(
            'http://www.w3.org/2000/svg',
            'svg'
        );
        svg.setAttribute('width', '16');
        svg.setAttribute('height', '16');
        svg.setAttribute('viewBox', '0 0 16 16');
        svg.setAttribute('fill', 'none');

        const path = document.createElementNS(
            'http://www.w3.org/2000/svg',
            'path'
        );
        path.setAttribute('fill-rule', 'evenodd');
        path.setAttribute('clip-rule', 'evenodd');
        path.setAttribute(
            'd',
            'M11 2H5V0.5C5 0.367392 4.94732 0.240253 4.85356 0.146484C4.75979 0.0527162 4.63261 0 4.5 0C4.36739 0 4.24021 0.0527162 4.14644 0.146484C4.05268 0.240253 4 0.367392 4 0.5V2H1.5C1.10228 2.00035 0.720925 2.15855 0.439697 2.43978C0.158469 2.72101 0.000353055 3.10228 0 3.5V14.5C0.000353055 14.8977 0.158469 15.279 0.439697 15.5602C0.720925 15.8414 1.10228 15.9996 1.5 16H14.5C14.8977 15.9996 15.279 15.8414 15.5603 15.5602C15.8415 15.279 15.9996 14.8977 16 14.5V3.5C15.9996 3.10228 15.8415 2.72101 15.5603 2.43978C15.279 2.15855 14.8977 2.00035 14.5 2H12V0.5C12 0.367392 11.9473 0.240253 11.8536 0.146484C11.7598 0.0527162 11.6326 0 11.5 0C11.3674 0 11.2402 0.0527162 11.1464 0.146484C11.0527 0.240253 11 0.367392 11 0.5V2ZM4.5 4.5C4.36739 4.5 4.24021 4.44728 4.14644 4.35352C4.05268 4.25975 4 4.13261 4 4V3H1.5C1.36745 3.00018 1.24038 3.05292 1.14665 3.14665C1.05292 3.24038 1.00018 3.36745 1 3.5V6H15V3.5C14.9998 3.36745 14.9471 3.24038 14.8534 3.14665C14.7596 3.05292 14.6326 3.00018 14.5 3H12V4C12 4.13261 11.9473 4.25975 11.8536 4.35352C11.7598 4.44728 11.6326 4.5 11.5 4.5C11.3674 4.5 11.2402 4.44728 11.1464 4.35352C11.0527 4.25975 11 4.13261 11 4V3H5V4C5 4.13261 4.94732 4.25975 4.85356 4.35352C4.75979 4.44728 4.63261 4.5 4.5 4.5ZM1 7H15V14.5C14.9998 14.6326 14.9471 14.7596 14.8534 14.8534C14.7596 14.9471 14.6326 14.9998 14.5 15H1.5C1.36745 14.9998 1.24038 14.9471 1.14665 14.8534C1.05292 14.7596 1.00018 14.6326 1 14.5V7Z'
        );
        path.setAttribute('fill', 'currentColor');
        svg.appendChild(path);

        button.appendChild(svg);

        groupDiv.appendChild(button);

        this.dialogDiv = document.createElement('div');
        this.dialogDiv.id = `${this.elementId}-datepicker-dialog`;
        this.dialogDiv.classList.add('datepicker-dialog');
        this.dialogDiv.role = 'dialog';
        this.dialogDiv.setAttribute('aria-modal', 'true');
        this.dialogDiv.setAttribute(
            'aria-label',
            this.localization.chooseDate || 'Choose date'
        );

        containerDiv.appendChild(this.dialogDiv);

        this.calendarsDiv = document.createElement('div');
        this.calendarsDiv.classList.add('calendars');

        this.dialogDiv.appendChild(this.calendarsDiv);

        const calendarContainer = document.createElement('div');
        calendarContainer.classList.add('calendar');

        this.createCalendar(calendarContainer);

        if (
            !this.hasAttribute('no-footer-buttons') ||
            this.getAttribute('no-footer-buttons') === 'false'
        ) {
            const buttonGroupDiv = document.createElement('div');
            buttonGroupDiv.classList.add('dialog-ok-cancel-group');

            const okButton = document.createElement('button');
            okButton.classList.add('dialog-button');
            okButton.value = 'ok';
            okButton.innerText = this.localization.ok || 'OK';

            buttonGroupDiv.appendChild(okButton);

            const cancelButton = document.createElement('button');
            cancelButton.classList.add('dialog-button');
            cancelButton.value = 'cancel';
            cancelButton.innerText = this.localization.cancel || 'Cancel';

            buttonGroupDiv.appendChild(cancelButton);

            this.dialogDiv.appendChild(buttonGroupDiv);
        }

        // Create live region for announcements
        const liveRegion = document.createElement('div');
        liveRegion.id = 'dialog-live-region';
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        liveRegion.className = 'sr-only';

        this.dialogDiv.appendChild(liveRegion);

        this.appendChild(containerDiv);

        if (this.hasAttribute('mindate')) {
            this.minDate = new Date(this.getAttribute('mindate'));
        }

        if (this.hasAttribute('maxdate')) {
            this.maxDate = new Date(this.getAttribute('maxdate'));
        }

        const options = {
            lang: this.lang === 'sv' ? 'sv-SE' : 'en-US',
            format: formatStr || defaultFormat,
            minDate: this.minDate,
            maxDate: this.maxDate,
            mondayFirstDayOfTheWeek: this.mondayFirstDayOfTheWeek,
            defaultDate: this.defaultDate,

            elementId: this.elementId,
            localization: this.localization,
        };

        this.datePickerDialog = new DatePickerDialog(containerDiv, options);

        this.input.addEventListener('change', (e) => this.handleInputChange(e));
        this.input.addEventListener('datepicker-dialog:change', (e) =>
            this.emitInputChange(e.target)
        );
    }

    disconnectedCallback() {
        this.innerHTML = '';
    }

    createCalendar(container) {
        if (!container) return;

        this.calendarsDiv.appendChild(container);

        const headerDiv = document.createElement('div');
        headerDiv.classList.add('header');

        container.appendChild(headerDiv);

        const prevYearButton = document.createElement('button');
        prevYearButton.type = 'button';
        prevYearButton.classList.add('prev-year');
        prevYearButton.setAttribute(
            'aria-label',
            this.localization.previousYear || 'previous year'
        );

        prevYearButton.innerHTML =
            '<?xml version="1.0" encoding="utf-8"?> \
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> \
            <path d="M18.3636 7.75736L16.9494 6.34315L11.2926 12L16.9494 17.6569L18.3636 16.2426L14.121 12L18.3636 7.75736Z" fill="currentColor"/> \
            <path d="M11.2926 6.34315L12.7068 7.75736L8.46417 12L12.7068 16.2426L11.2926 17.6568L5.63574 12L11.2926 6.34315Z" fill="currentColor"/> \
            </svg>';

        headerDiv.appendChild(prevYearButton);

        const prevMonthButton = document.createElement('button');
        prevMonthButton.type = 'button';
        prevMonthButton.classList.add('prev-month');
        prevMonthButton.setAttribute(
            'aria-label',
            this.localization.previousMonth || 'previous month'
        );

        prevMonthButton.innerHTML =
            '<?xml version="1.0" encoding="utf-8"?>\
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\
            <path d="M15 6L9 12L15 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>\
            </svg>';

        headerDiv.appendChild(prevMonthButton);

        const monthYearContainer = document.createElement('div');
        monthYearContainer.classList.add('month-year-container');

        const monthYearHeader = document.createElement('h2');
        monthYearHeader.id = 'id-grid-label';
        monthYearHeader.classList.add('month-year');
        monthYearHeader.setAttribute('aria-live', 'polite');

        monthYearContainer.appendChild(monthYearHeader);
        headerDiv.appendChild(monthYearContainer);

        const nextMonthButton = document.createElement('button');
        nextMonthButton.type = 'button';
        nextMonthButton.classList.add('next-month');
        nextMonthButton.setAttribute(
            'aria-label',
            this.localization.nextMonth || 'next month'
        );

        nextMonthButton.innerHTML =
            '<?xml version="1.0" encoding="utf-8"?> \
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> \
            <path d="M9 6L15 12L9 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/> \
            </svg>';

        headerDiv.appendChild(nextMonthButton);

        const nextYearButton = document.createElement('button');
        nextYearButton.type = 'button';
        nextYearButton.classList.add('next-year');
        nextYearButton.setAttribute(
            'aria-label',
            this.localization.nextYear || 'next year'
        );

        nextYearButton.innerHTML =
            '<?xml version="1.0" encoding="utf-8"?> \
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> \
                <path d="M5.63574 7.75737L7.04996 6.34315L12.7068 12L7.04998 17.6568L5.63577 16.2426L9.87839 12L5.63574 7.75737Z" fill="currentColor" /> \
                <path d="M12.7068 6.34315L11.2926 7.75737L15.5352 12L11.2926 16.2426L12.7068 17.6568L18.3637 12L12.7068 6.34315Z" fill="currentColor" /> \
            </svg>';

        headerDiv.appendChild(nextYearButton);

        const tableWrapper = document.createElement('div');
        tableWrapper.classList.add('table-wrap');

        container.appendChild(tableWrapper);

        const table = document.createElement('table');
        table.classList.add('dates');
        table.role = 'grid';
        table.setAttribute('aria-labelledby', 'id-grid-label');

        tableWrapper.appendChild(table);

        const thead = document.createElement('thead');
        const trThead = document.createElement('tr');

        thead.appendChild(trThead);
        table.appendChild(thead);

        const dayNames = getAllDayNames({ locale: this.lang }).map((d) => ({
            long: d.long[0].toUpperCase() + d.long.slice(1),
            short: d.short[0].toUpperCase() + d.short.slice(1, 3),
        }));
        this.months = getAllMonthNames({ locale: this.lang });

        const locDays = this.mondayFirstDayOfTheWeek
            ? [...dayNames.slice(1), dayNames[0]]
            : dayNames;

        for (let day of locDays) {
            const th = document.createElement('th');
            th.scope = 'col';
            th.abbr = day.long;
            th.role = 'columnheader';
            th.setAttribute('aria-label', day.long);
            th.innerText = day.short;
            trThead.appendChild(th);
        }

        const tbody = document.createElement('tbody');

        table.appendChild(tbody);
    }

    attributeChangedCallback(attrName, oldValue, newValue) {
        if (oldValue === newValue) return;

        switch (attrName) {
            case 'mindate':
                this.minDate = new Date(newValue);
                if (this.datePickerDialog?.setOptions) {
                    this.datePickerDialog.setOptions({ minDate: this.minDate });
                }
                break;

            case 'maxdate':
                this.maxDate = new Date(newValue);
                if (this.datePickerDialog?.setOptions) {
                    this.datePickerDialog.setOptions({ maxDate: this.maxDate });
                }
                break;
            case 'default-date':
                if (newValue === '') {
                    this.datePickerDialog?.setDate(this.defaultDate);
                } else {
                    this.defaultDate = newValue;
                    this.datePickerDialog?.setDate(newValue);
                }
                break;
            case 'format':
                this.format = newValue;
                if (this.datePickerDialog?.setOptions) {
                    this.datePickerDialog.setOptions({ format: this.format });
                }
                break;
            case 'placeholder':
                if (this.useFloatingLabel && this.inputLabel) {
                    if (newValue != null && newValue.trim() != '') {
                        this.inputLabel.textContent = newValue.trim();
                        if (this.inputLabel.classList.contains('sr-only')) {
                            this.inputLabel.classList.remove('sr-only');
                        }
                    } else {
                        this.inputLabel.textContent = '';
                    }
                } else {
                    if (this.input) this.input.placeholder = newValue.trim();
                }
                break;
            case 'valid-formats':
                this.validFormats = newValue.split(',').map((f) => f.trim());
                break;
            case 'localization': {
                const validJsonLocalization = newValue
                    .replace(/(\w+):/g, '"$1":')
                    .replace(/'/g, '"');
                const updatedLocalization = JSON.parse(validJsonLocalization);
                this.updateLocalization(updatedLocalization);
            }
        }
    }

    applyClasses(newValue) {
        if (!this.input) return;

        this.input.className = '';

        if (newValue) {
            const newValueAsArr = newValue.split(' ');
            for (let newClass of newValueAsArr) {
                this.input.classList.add(newClass);
            }
        }
    }

    handleInputChange(e) {
        const dateStr = e.target.value;

        if (dateStr === '') {
            if (this.defaultDate !== null) {
                this.datePickerDialog.setDate(this.defaultDate);
                this.emitInputChange(this.input);
            } else {
                this.inputValue = null;
                this.datePickerDialog.date = null;
                this.emitInputChange(this.input);
                this.input.classList.remove('has-value');
            }
        } else {
            const parsedDate = this.parseDate(dateStr);
            const isValid = this.isValid(parsedDate, this.format);

            if (parsedDate && isValid) {
                this.inputValue = dateStr;
                this.datePickerDialog.setDate(parsedDate);
                this.emitInputChange(this.input);
            } else if (parsedDate && !isValid) {
                if (this.maxDate) {
                    if (this.maxDate < parsedDate) {
                        this.datePickerDialog.setDate(this.maxDate);
                        this.emitInputChange(this.input);
                    }
                }
                if (this.minDate) {
                    if (this.minDate > parsedDate) {
                        this.datePickerDialog.setDate(this.minDate);
                        this.emitInputChange(this.input);
                    }
                }
            } else {
                e.target.value = '';
                if (this.defaultDate !== null) {
                    this.datePickerDialog.setDate(this.defaultDate);
                    this.emitInputChange(this.input);
                } else {
                    this.inputValue = null;
                    this.datePickerDialog.clearDate();
                    this.emitInputChange(this.input);
                }
            }
        }
    }

    formatDate(date, format) {
        const map = {
            YYYY: date.getFullYear(),
            YY: date.getFullYear(),
            MM: String(date.getMonth() + 1).padStart(2, '0'),
            MMMM: this.monthLabels[this.lang][date.getMonth()],
            DD: String(date.getDate()).padStart(2, '0'),
            D: String(date.getDate()),
            HH: String(date.getHours()).padStart(2, '0'),
            mm: String(date.getMinutes()).padStart(2, '0'),
            ss: String(date.getSeconds()).padStart(2, '0'),
        };

        return format.replace(
            /YYYY|YY|MM|MMMM|DD|D|HH|mm|ss/g,
            (matched) => map[matched]
        );
    }

    parseDate(dateStr) {
        let validFormats = this.format
            ? [this.format, ...this.validFormats]
            : this.validFormats;

        for (let fmt of validFormats) {
            const currentYear = new Date().getFullYear();

            let regexPattern = fmt.replace(/([.*+?^=!:${}()|[\]/\\])/g, '\\$1');

            const strictRegexMap = {
                YYYY: '(\\d{4})',
                YY: '(\\d{2})',
                MMMM: '([A-Za-z]+)',
                MM: '(\\d{2})',
                DD: '(\\d{2})',
                D: '(\\d{1,2})',
            };

            regexPattern = regexPattern.replace(
                /(YYYY|YY|MMMM|MM|DD|D)/g,
                (token) => strictRegexMap[token] || token
            );

            const regex = new RegExp(`^${regexPattern}$`);
            const match = dateStr.match(regex);

            if (match) {
                const formatParts = [];
                const formatRegex = /(YYYY|YY|MMMM|MM|DD|D)/g;
                let m;
                while ((m = formatRegex.exec(fmt)) !== null) {
                    formatParts.push(m[0]);
                }

                let year, month, day;
                let matchIdx = 1;
                for (const part of formatParts) {
                    if (part === 'YYYY') year = parseInt(match[matchIdx], 10);
                    else if (part === 'YY')
                        year =
                            (parseInt(match[matchIdx], 10) + 2000 > currentYear
                                ? 1900
                                : 2000) +
                            parseInt(parseInt(match[matchIdx], 10), 10);
                    else if (part === 'MM')
                        month = parseInt(match[matchIdx], 10);
                    else if (part === 'DD' || part === 'D')
                        day = parseInt(match[matchIdx], 10);
                    else if (part === 'MMMM') {
                        const monthName = match[matchIdx];
                        month = this.months.findIndex(
                            (m) => m.toLowerCase() === monthName.toLowerCase()
                        );
                        if (month !== -1) month += 1;
                    }
                    matchIdx++;
                }

                if (year && month && day) {
                    const maxDay = new Date(year, month, 0).getDate();
                    if (month === -1) continue;
                    if (month > 12) continue;
                    if (day > maxDay) continue;

                    return new Date(year, month - 1, day);
                }
            }
        }
        return null;
    }

    validate(domNode) {
        if (!this.validate || !domNode) return;

        const value = domNode.value;
        if (this.isValid(value, this.format)) {
            this.input.setCustomValidity('');
        } else {
            this.input.setCustomValidity('Invalid date format');
        }
    }

    isValid(date) {
        if (this.minDate) {
            if (date < this.minDate) return false;
        }
        if (this.maxDate) {
            if (date > this.maxDate) return false;
        }

        return true;
    }

    emit(type, detail = {}) {
        let event = new CustomEvent(`datepicker:${type}`, {
            bubbles: true,
            cancelable: true,
            detail: detail,
        });

        this.dispatchEvent(event);
    }

    emitInputChange(input) {
        if (!input) return;

        if (input === this.input) {
            this.emit('input-change', {
                value: this.input.value,
                valueAsDate: this.parseDate(this.input.value),
            });
        }
    }

    updateElementLocalization(element, newLocalization) {
        switch (element) {
            case 'openCalendar': {
                const buttonSpan = this.querySelector('button.icon span');
                if (buttonSpan) {
                    buttonSpan.textContent = newLocalization || 'Calendar';
                }
                break;
            }
            case 'chooseDate': {
                const chooseDateButton =
                    this.querySelector('button.choose-date');
                if (chooseDateButton) {
                    chooseDateButton.setAttribute(
                        'aria-label',
                        newLocalization || 'Choose date'
                    );
                }
                break;
            }
        }
    }

    updateLocalization(newLocalization) {
        if (!this.localization) {
            this.localization = {};
        }

        const updatedLocalization = {};
        for (let key in newLocalization) {
            if (
                Object.prototype.hasOwnProperty.call(this.localization, key) ===
                    false ||
                this.localization[key] !== newLocalization[key]
            ) {
                updatedLocalization[key] = newLocalization[key];
                this.updateElementLocalization(key, newLocalization[key]);
            }
        }

        if (this.datePickerDialog) {
            this.datePickerDialog.updateLocalization(updatedLocalization);
        }

        this.localization = { ...this.localization, ...updatedLocalization };
    }
}

customElements.define('svea-datepicker', DatePicker);
